import { SelectItem } from '../types';
import * as Constants from './constants';

export const isElectron = () =>
  window.navigator.userAgent.search(/electron/i) !== -1;

export const getHostname = (): string => {
  return window.location.hostname === '127.0.0.1'
    ? 'localhost'
    : window.location.hostname?.toLowerCase() ?? '';
};

export const getQueryVariable = <T extends string>(
  variable: string
): T | undefined => {
  const query = window.location.search.substring(1);
  const vars = query.split('&');
  for (const varPairs of vars) {
    const pair = varPairs.split('=');
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1]) as T;
    }
  }

  return undefined;
};

// This works for one screen but I'll have to adjust for two or more.
// I think the biggest issue is that we don't have access to the parent parent frame to get
// the window size like MS does with it's Teams library code...
// So we'll have to make some assumptions and/or just get close.
export const openCenteredPopup = (url?: string): Window | null => {
  const top = window.outerHeight / 2 - 300;
  const left = window.outerWidth / 2 - 200;

  return window.open(
    `/home/authorizeExternal?authUrl=${url}&returnOrigin=${encodeURIComponent(
      window.location.origin
    )}`,
    'bentley-teams-auth',
    `width=400,height=600,top=${top},left=${left}`
  );
};

export const isConnectionIdValid = (connectionId: string): boolean =>
  !['shareglobal', 'allConnections'].includes(connectionId);

export const getTeamsSettings =
  async (): Promise<microsoftTeams.settings.Settings> => {
    return new Promise((resolve, reject) =>
      microsoftTeams.settings.getSettings(settings => resolve(settings))
    );
  };

export const setTeamsSettings = async (
  instanceSettings: microsoftTeams.settings.Settings
): Promise<{ status: boolean; reason?: string }> => {
  return new Promise<{ status: boolean; reason?: string }>(
    (resolve, reject) => {
      microsoftTeams.settings.setSettings(instanceSettings, (status, reason) =>
        resolve({ status, reason })
      );
    }
  );
};

export const getTeamsContext = async (): Promise<microsoftTeams.Context> => {
  return new Promise((resolve, reject) =>
    microsoftTeams.getContext(context => resolve(context))
  );
};

export const getTeamsTabs = async (): Promise<microsoftTeams.TabInstance[]> => {
  const tabInstanceParameters = {
    favoriteChannelsOnly: false,
    favoriteTeamsOnly: false,
  } as microsoftTeams.TabInstanceParameters;

  return new Promise((resolve, reject) =>
    microsoftTeams.getTabInstances(
      tabInfo => resolve(tabInfo.teamTabs),
      tabInstanceParameters
    )
  );
};

export const getEntityId = async (): Promise<string | undefined> => {
  const context = await getTeamsContext();
  if (context.entityId) {
    try {
      safeBase64Decode(context.entityId);
    } catch {
      return context.entityId;
    }
  }
};

export const safeBase64Encode = (data: string): string => {
  return btoa(encodeURIComponent(data));
};

export const safeBase64Decode = (encodedData: string): string => {
  return decodeURIComponent(atob(encodedData));
};

export const getLinkToTab = async (entityId?: string): Promise<string> => {
  const appId = Constants.Teams.appId;
  const context = await getTeamsContext();
  const channelContext = `{"channelId":"${context.channelId}"}`;

  return `https://teams.microsoft.com/l/entity/${appId}/${encodeURIComponent(
    entityId ?? context.entityId
  )}?context=${encodeURIComponent(channelContext)}`;
};

export const waitAndBackoffRetry = async <T>(
  promise: Promise<T> | (() => Promise<T>),
  timeout: number,
  maxAttempts: number,
  onAttempt?: (err?: any) => any
): Promise<T | undefined> => {
  let attempts = 0;
  while (attempts < maxAttempts) {
    try {
      const value = (await Promise.race([
        typeof promise === 'function' ? promise() : promise,
        new Promise((_, reject) => setTimeout(reject, timeout)),
      ])) as T;
      return value;
    } catch (error) {
      attempts++;
      onAttempt && attempts < maxAttempts && onAttempt(error);
    }
  }
};

export const embedPwWebComponent = (serviceName?: string): boolean =>
  serviceName === 'ProjectWise Web Connections';

export const embedShareComponent = (
  serviceName?: string,
  connectionName?: string
): boolean =>
  (serviceName === 'Share' || serviceName === 'ProjectWise Share') &&
  (connectionName === 'Share' || !connectionName);

export const filterSelectItems = (items: SelectItem[], filter: string) =>
  filter
    ? items.filter(item =>
        item.text?.toLowerCase().includes(filter.toLowerCase())
      )
    : items;
